import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    FormControlLabel,
    Alert,
    CircularProgress
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { tr } from 'date-fns/locale';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { announcementService, CreateAnnouncementData } from '../../services/announcementService';
import { Announcement } from '../../types/announcement';
import { format } from 'date-fns';

const AnnouncementsAdmin: React.FC = () => {
    const [announcements, setAnnouncements] = useState<Announcement[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedId, setSelectedId] = useState<string | null>(null);

    const [formData, setFormData] = useState<CreateAnnouncementData>({
        title: '',
        content: '',
        type: 'info',
        startDate: new Date(),
        endDate: null,
        isActive: true
    });

    useEffect(() => {
        loadAnnouncements();
    }, []);

    const loadAnnouncements = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await announcementService.getAllAnnouncements();
            setAnnouncements(data);
        } catch (error) {
            setError('Duyurular yüklenirken bir hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (announcement?: Announcement) => {
        if (announcement) {
            setSelectedId(announcement.id);
            setFormData({
                title: announcement.title,
                content: announcement.content,
                type: announcement.type,
                startDate: new Date(announcement.startDate),
                endDate: announcement.endDate ? new Date(announcement.endDate) : null,
                isActive: announcement.isActive
            });
        } else {
            setSelectedId(null);
            setFormData({
                title: '',
                content: '',
                type: 'info',
                startDate: new Date(),
                endDate: null,
                isActive: true
            });
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setSelectedId(null);
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);
            if (selectedId) {
                await announcementService.updateAnnouncement(selectedId, formData);
            } else {
                await announcementService.createAnnouncement(formData);
            }
            await loadAnnouncements();
            handleCloseDialog();
        } catch (error) {
            setError('Duyuru kaydedilirken bir hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: string) => {
        try {
            setLoading(true);
            await announcementService.deleteAnnouncement(id);
            await loadAnnouncements();
        } catch (error) {
            setError('Duyuru silinirken bir hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={tr}>
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                    <Typography variant="h4" component="h1">
                        Duyuru Yönetimi
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleOpenDialog()}
                    >
                        Yeni Duyuru
                    </Button>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Başlık</TableCell>
                                <TableCell>İçerik</TableCell>
                                <TableCell>Tip</TableCell>
                                <TableCell>Başlangıç</TableCell>
                                <TableCell>Bitiş</TableCell>
                                <TableCell>Durum</TableCell>
                                <TableCell>İşlemler</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : announcements.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        Henüz duyuru bulunmuyor.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                announcements.map((announcement) => (
                                    <TableRow key={announcement.id}>
                                        <TableCell>{announcement.title}</TableCell>
                                        <TableCell>{announcement.content}</TableCell>
                                        <TableCell>{announcement.type}</TableCell>
                                        <TableCell>
                                            {format(new Date(announcement.startDate), 'dd.MM.yyyy HH:mm')}
                                        </TableCell>
                                        <TableCell>
                                            {announcement.endDate
                                                ? format(new Date(announcement.endDate), 'dd.MM.yyyy HH:mm')
                                                : '-'}
                                        </TableCell>
                                        <TableCell>
                                            {announcement.isActive ? 'Aktif' : 'Pasif'}
                                        </TableCell>
                                        <TableCell>
                                            <IconButton
                                                onClick={() => handleOpenDialog(announcement)}
                                                color="primary"
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                onClick={() => handleDelete(announcement.id)}
                                                color="error"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                    <DialogTitle>
                        {selectedId ? 'Duyuru Düzenle' : 'Yeni Duyuru'}
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                            <TextField
                                label="Başlık"
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                fullWidth
                            />

                            <TextField
                                label="İçerik"
                                value={formData.content}
                                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                                multiline
                                rows={4}
                                fullWidth
                            />

                            <FormControl fullWidth>
                                <InputLabel>Tip</InputLabel>
                                <Select
                                    value={formData.type}
                                    onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                                    label="Tip"
                                >
                                    <MenuItem value="info">Bilgi</MenuItem>
                                    <MenuItem value="warning">Uyarı</MenuItem>
                                    <MenuItem value="error">Hata</MenuItem>
                                    <MenuItem value="success">Başarılı</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.isActive}
                                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                    />
                                }
                                label="Aktif"
                            />

                            <DateTimePicker
                                label="Başlangıç Tarihi"
                                value={formData.startDate}
                                onChange={(newValue) => setFormData({ ...formData, startDate: newValue || new Date() })}
                            />

                            <DateTimePicker
                                label="Bitiş Tarihi (Opsiyonel)"
                                value={formData.endDate}
                                onChange={(newValue) => setFormData({ ...formData, endDate: newValue })}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog}>İptal</Button>
                        <Button
                            onClick={handleSubmit}
                            variant="contained"
                            color="primary"
                            disabled={loading}
                        >
                            {loading ? <CircularProgress size={24} /> : 'Kaydet'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </LocalizationProvider>
    );
};

export default AnnouncementsAdmin; 